import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Cookie, Shield, Settings, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';

const CookiePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <Cookie className="w-8 h-8 text-orange-600 mr-3" />
            <h1 className="text-3xl font-bold" style={{ color: '#000000' }}>Cookie Policy</h1>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="mb-6" style={{ color: '#333333' }}>
              <strong>Effective Date:</strong> October 15, 2025<br />
              <strong>Last Updated:</strong> October 15, 2025
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>What Are Cookies</h2>
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
              <p style={{ color: '#000000' }}>
                Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling certain functionality.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Types of Cookies We Use</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <div className="flex items-center mb-4">
                  <Shield className="w-8 h-8 text-red-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Essential Cookies</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>These cookies are necessary for the website to function properly:</p>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Authentication cookies: Keep you logged in</li>
                  <li>Security cookies: Protect against fraud and abuse</li>
                  <li>Session cookies: Maintain your session state</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Functional Cookies</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>These cookies enhance your experience:</p>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Preference cookies: Remember your settings</li>
                  <li>Language cookies: Remember your language choice</li>
                  <li>Theme cookies: Remember your display preferences</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-center mb-4">
                  <BarChart3 className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Analytics Cookies</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>These cookies help us understand how you use our service:</p>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Usage analytics: Track feature usage and performance</li>
                  <li>Error tracking: Help us identify and fix issues</li>
                  <li>Performance monitoring: Monitor site performance</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Managing Cookies</h2>
            
            <div className="bg-gray-100 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#000000' }}>You can control cookies through your browser settings:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>✅ Accept All Cookies</h4>
                  <p className="text-sm" style={{ color: '#333333' }}>Allow all cookies for full functionality</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>❌ Block All Cookies</h4>
                  <p className="text-sm" style={{ color: '#333333' }}>May limit website functionality</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>⚙️ Selective Blocking</h4>
                  <p className="text-sm" style={{ color: '#333333' }}>Choose which types to allow</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Third-Party Cookies</h2>
            
            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#000000' }}>We may use third-party services that set their own cookies:</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>Google Analytics</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>For usage analytics and performance monitoring</p>
                  </div>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Analytics</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>AWS CloudFront</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>For content delivery and performance</p>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Performance</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>Social Media Platforms</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>When you connect your social media accounts</p>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Integration</span>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Cookie Details</h2>
            
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#000000' }}>Cookie Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#000000' }}>Purpose</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#000000' }}>Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#000000' }}>Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: '#000000' }}>auth_token</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>User authentication</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>7 days</td>
                    <td className="px-4 py-3 text-sm"><span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: '#000000' }}>user_preferences</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>Store user settings</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>30 days</td>
                    <td className="px-4 py-3 text-sm"><span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Functional</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: '#000000' }}>analytics_id</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>Track usage patterns</td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#000000' }}>2 years</td>
                    <td className="px-4 py-3 text-sm"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Analytics</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Browser-Specific Instructions</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Chrome</h3>
                <ol className="list-decimal list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Click the three dots menu</li>
                  <li>Go to Settings → Privacy and security</li>
                  <li>Click "Cookies and other site data"</li>
                  <li>Choose your cookie preferences</li>
                </ol>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Firefox</h3>
                <ol className="list-decimal list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Click the menu button</li>
                  <li>Go to Settings → Privacy & Security</li>
                  <li>Find "Cookies and Site Data"</li>
                  <li>Adjust your cookie settings</li>
                </ol>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Safari</h3>
                <ol className="list-decimal list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Go to Safari → Preferences</li>
                  <li>Click the Privacy tab</li>
                  <li>Choose "Block all cookies" or customize</li>
                  <li>Close preferences to save</li>
                </ol>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Edge</h3>
                <ol className="list-decimal list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Click the three dots menu</li>
                  <li>Go to Settings → Cookies and site permissions</li>
                  <li>Click "Cookies and site data"</li>
                  <li>Configure your preferences</li>
                </ol>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Contact Us</h2>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="mb-4" style={{ color: '#000000' }}>
                For questions about our cookie policy:
              </p>
              <div className="space-y-1" style={{ color: '#000000' }}>
                <p><strong>Email:</strong> info@quixocial.com</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-orange-100 rounded-lg">
              <p className="text-sm text-center" style={{ color: '#000000' }}>
                This Cookie Policy helps you understand how we use cookies to improve your experience on Quixocial.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;